import React, { useContext, useEffect, useState } from 'react';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import { formatMoney, useLocalTable } from '../finanzas-cxp/shared.js';

/**
 * V1: `IbuFront/src/components/views/Reportes/ReporteFacturas.jsx`, gateada por
 * `user.facturasReporte` -> `reportes:facturas`. Fuente: Anticipos del rango de
 * fechas con `numeroComisionCliente > 0`, agrupados por cliente (Cobrado, Pagado,
 * Utilidad) -- backend nuevo: `GET /reportes/facturas/:fechaInicio/:fechaFin`, ver
 * `reportes-extra.service.js#facturas`. Mismo criterio que
 * `ReporteTransferenciasPage` sobre exportación PDF/Excel/eMail: no se replica (V2
 * aún no la tiene en ningún reporte).
 */
export default function ReporteFacturasPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('reportes:facturas');

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  const [fechaInicio, setFechaInicio] = useState(inicio.toISOString().slice(0, 10));
  const [fechaFin, setFechaFin] = useState(hoy.toISOString().slice(0, 10));

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);

  useEffect(() => {
    if (!puedeVer || !fechaInicio || !fechaFin) return;
    setCargando(true);
    setError(null);
    apiClient
      .get(`/reportes/facturas/${fechaInicio}/${fechaFin}`)
      .then(({ data }) => setFilas((data.data || []).map((f) => ({ ...f, _id: f.idCliente || f.cliente }))))
      .catch((e) => { setError(e.message); setFilas([]); })
      .finally(() => setCargando(false));
  }, [puedeVer, fechaInicio, fechaFin]);

  // V1: búsqueda por cliente/cobrado/pagado/utilidad, 50 por página, sin orden por
  // defecto (orden de primera aparición por fecha desc, ya resuelto en el backend).
  const { search, setSearch, page, setPage, pageRows, filtradas, sortBy, sortOrder, ordenarPor, pagination } = useLocalTable(filas, {
    searchFields: ['cliente', 'cobrado', 'pagado', 'utilidad'],
    perPage: 50,
  });

  const totalCobrado = filtradas.reduce((t, f) => t + (f.cobrado || 0), 0);
  const totalPagado = filtradas.reduce((t, f) => t + (f.pagado || 0), 0);
  const totalUtilidad = filtradas.reduce((t, f) => t + (f.utilidad || 0), 0);

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  const columnas = [
    { key: 'cliente', label: 'Cliente', sortable: true },
    { key: 'cobrado', label: 'Cobrado', sortable: true, className: 'text-right', render: (r) => formatMoney(r.cobrado) },
    { key: 'pagado', label: 'Pagado', sortable: true, className: 'text-right', render: (r) => formatMoney(r.pagado) },
    { key: 'utilidad', label: 'Utilidad', sortable: true, className: 'text-right', render: (r) => formatMoney(r.utilidad) },
  ];

  const columnasExport = [
    { key: 'cliente', label: 'Cliente' },
    { key: 'cobrado', label: 'Cobrado', render: (r) => formatMoney(r.cobrado) },
    { key: 'pagado', label: 'Pagado', render: (r) => formatMoney(r.pagado) },
    { key: 'utilidad', label: 'Utilidad', render: (r) => formatMoney(r.utilidad) },
  ];
  const columnasExcel = columnasExport.map((c) => ({ key: c.key, label: c.label }));
  const opcionesPdf = {
    fileName: 'ReporteFacturas',
    totalCols: { cliente: 'Total', cobrado: formatMoney(totalCobrado), pagado: formatMoney(totalPagado), utilidad: formatMoney(totalUtilidad) },
  };
  const hacerPdf = () => exportTablePdf('Reporte de Facturas', columnasExport, filtradas, opcionesPdf);
  const hacerExcel = () => exportTableExcel('Reporte de Facturas', columnasExcel, filtradas, {
    fileName: `ReporteFacturas-${fechaInicio}-${fechaFin}`, sheetName: 'ReporteFacturas',
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Reporte de Facturas</h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={hacerPdf} className="btn-outline text-xs px-3 py-1.5">PDF</button>
          <button type="button" onClick={() => setMailOpen(true)} className="btn-outline text-xs px-3 py-1.5">eMail</button>
          <button type="button" onClick={hacerExcel} className="btn-outline text-xs px-3 py-1.5">Excel</button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="input-base" />
        </div>
        <div className="ml-auto">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="input-base w-60" />
        </div>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable
        columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor} rowKey="idCliente"
        footer={{ cliente: 'TOTAL', cobrado: formatMoney(totalCobrado), pagado: formatMoney(totalPagado), utilidad: formatMoney(totalUtilidad) }}
      />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Reporte de Facturas"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Reporte de Facturas', columnasExport, filtradas, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Reporte de Facturas', email, fileName: 'ReporteFacturas.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el Reporte de Facturas.'),
          });
        }}
      />
    </div>
  );
}
